import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getTwoDigitMonth(date: Date) {
  return ("0" + (date.getMonth() + 1)).slice(-2);
}

export function getTwoDigitDay(date: Date) {
  return ("0" + (date.getDate())).slice(-2);
}

export function getHour(time: string) {
  return time.substring(0, 2);
}

export function getMinute(time: string) {
  return time.substring(3, 5);
}

export function debounce<T extends (...args: any[]) => void>(fn: T, delay = 300) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

const currencySymbolMap: Record<string, string> = {
  MYR: "RM",
  THB: "฿",
};

export function formatCurrencySymbol(currencyCode: string): string {
  const normalizedCode = currencyCode.trim();
  return currencySymbolMap[normalizedCode] || normalizedCode;
}

type CurrencyPosition = "front" | "back";

interface CurrencyFormatOptions {
  thousandsSeparator?: string;
  decimalSeparator?: string;
  currencyPosition?: CurrencyPosition;
}

function getPriceFormatOptions(): Required<CurrencyFormatOptions> {
  const configuredPosition = import.meta.env.VITE_CURRENCY_POSITION?.toLowerCase();

  return {
    thousandsSeparator: import.meta.env.VITE_PRICE_THOUSANDS_SEPARATOR ?? ",",
    decimalSeparator: import.meta.env.VITE_PRICE_DECIMAL_SEPARATOR ?? ".",
    currencyPosition: configuredPosition === "back" ? "back" : "front",
  };
}

export function formatCurrencyAmount(
  amount: number,
  currencyCode: string,
  options: CurrencyFormatOptions = {},
): string {
  const formatOptions = { ...getPriceFormatOptions(), ...options };
  const [integerPart, fractionPart] = (Number.isFinite(amount) ? amount : 0)
    .toFixed(2)
    .split(".");
  const groupedInteger = formatOptions.thousandsSeparator
    ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, formatOptions.thousandsSeparator)
    : integerPart;
  const formattedAmount = `${groupedInteger}${formatOptions.decimalSeparator}${fractionPart}`;
  const symbol = formatCurrencySymbol(currencyCode);

  return formatOptions.currencyPosition === "back"
    ? `${formattedAmount} ${symbol}`
    : `${symbol} ${formattedAmount}`;
}
